/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Selectors for Settings Profile component
 *
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.26.0
 * @since 1.3.0
 */

import { createSelector, OutputSelector } from 'reselect';

import { IRootReducer } from '@reducers/rootReducer';

import { ESettingsProfileComponents } from './settingsProfileConstants';
import { ISettingsProfileReducer } from './settingsProfileReducer';
import {
    ISettingsProfileMetaContacts,
    ISettingsProfileMetaHints,
    ISettingsProfileMetaPassword,
    ISettingsProfileMetaQuestion,
    ISettingsProfileParamsContacts,
    ISettingsProfileParamsHints,
    ISettingsProfileParamsOnboarding,
    ISettingsProfileParamsPassword,
    ISettingsProfileParamsQuestion,
    ISettingsProfilePreferredLK,
} from './settingsProfileTypes';

export const makeSelectSettingsProfileState = (state: IRootReducer): ISettingsProfileReducer => state.settings.profile;

/*
 * -------------------------------------------
 *           IS COMMON PARAMS UPDATED
 * -------------------------------------------
 */

export const makeSelectSettingsProfileIsCommonParamsUpdated = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: ISettingsProfileReducer) => boolean
> => createSelector(makeSelectSettingsProfileState, (state) => state.isCommonParamsUpdated);

/*
 * -------------------------------------------
 *           IS PREFERRED LK PARAMS UPDATED
 * -------------------------------------------
 */

export const makeSelectSettingsProfileIsPreferredLKParamsUpdated = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: ISettingsProfileReducer) => boolean
> => createSelector(makeSelectSettingsProfileState, (state) => state.isPreferredLKParamsUpdated);

/*
 * -------------------------------------------
 *           IS COMMON PARAMS VALID
 * -------------------------------------------
 */

export const makeSelectSettingsProfileIsCommonParamsValid = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: ISettingsProfileReducer) => boolean
> => createSelector(makeSelectSettingsProfileState, (state) => state.isCommonParamsValid);

/*
 * -------------------------------------------
 *           PROFILE META
 * -------------------------------------------
 */

export const makeSelectSettingsProfileMetaContacts = (): OutputSelector<
    IRootReducer,
    ISettingsProfileMetaContacts,
    (state: ISettingsProfileReducer) => ISettingsProfileMetaContacts
> => createSelector(makeSelectSettingsProfileState, (state) => state.meta[ESettingsProfileComponents.CONTACTS]);

export const makeSelectSettingsProfileMetaPreferredLK = (): OutputSelector<
    IRootReducer,
    ISettingsProfilePreferredLK,
    (state: ISettingsProfileReducer) => ISettingsProfilePreferredLK
> => createSelector(makeSelectSettingsProfileState, (state) => state.meta[ESettingsProfileComponents.PREFERRED_LK]);

export const makeSelectSettingsProfileMetaHints = (): OutputSelector<
    IRootReducer,
    ISettingsProfileMetaHints,
    (state: ISettingsProfileReducer) => ISettingsProfileMetaHints
> => createSelector(makeSelectSettingsProfileState, (state) => state.meta[ESettingsProfileComponents.HINTS]);

export const makeSelectSettingsProfileMetaOnboarding = (): OutputSelector<
    IRootReducer,
    ISettingsProfileParamsOnboarding,
    (state: ISettingsProfileReducer) => ISettingsProfileParamsOnboarding
> => createSelector(makeSelectSettingsProfileState, (state) => state.meta[ESettingsProfileComponents.ONBOARDING]);

export const makeSelectSettingsProfileMetaPassword = (): OutputSelector<
    IRootReducer,
    ISettingsProfileMetaPassword,
    (state: ISettingsProfileReducer) => ISettingsProfileMetaPassword
> => createSelector(makeSelectSettingsProfileState, (state) => state.meta[ESettingsProfileComponents.PASSWORD]);

export const makeSelectSettingsProfileMetaQuestion = (): OutputSelector<
    IRootReducer,
    ISettingsProfileMetaQuestion,
    (state: ISettingsProfileReducer) => ISettingsProfileMetaQuestion
> => createSelector(makeSelectSettingsProfileState, (state) => state.meta[ESettingsProfileComponents.QUESTION]);

/*
 * -------------------------------------------
 *           PROFILE PARAMS
 * -------------------------------------------
 */

export const makeSelectSettingsProfileParamsContacts = (): OutputSelector<
    IRootReducer,
    ISettingsProfileParamsContacts,
    (state: ISettingsProfileReducer) => ISettingsProfileParamsContacts
> => createSelector(makeSelectSettingsProfileState, (state) => state.params[ESettingsProfileComponents.CONTACTS]);

export const makeSelectSettingsProfileParamsPreferredLK = (): OutputSelector<
    IRootReducer,
    ISettingsProfilePreferredLK,
    (state: ISettingsProfileReducer) => ISettingsProfilePreferredLK
> => createSelector(makeSelectSettingsProfileState, (state) => state.params[ESettingsProfileComponents.PREFERRED_LK]);

export const makeSelectSettingsProfileParamsHints = (): OutputSelector<
    IRootReducer,
    ISettingsProfileParamsHints,
    (state: ISettingsProfileReducer) => ISettingsProfileParamsHints
> => createSelector(makeSelectSettingsProfileState, (state) => state.params[ESettingsProfileComponents.HINTS]);

export const makeSelectSettingsProfileParamsOnboarding = (): OutputSelector<
    IRootReducer,
    ISettingsProfileParamsOnboarding,
    (state: ISettingsProfileReducer) => ISettingsProfileParamsOnboarding
> => createSelector(makeSelectSettingsProfileState, (state) => state.params[ESettingsProfileComponents.ONBOARDING]);

export const makeSelectSettingsProfileParamsPassword = (): OutputSelector<
    IRootReducer,
    ISettingsProfileParamsPassword,
    (state: ISettingsProfileReducer) => ISettingsProfileParamsPassword
> => createSelector(makeSelectSettingsProfileState, (state) => state.params[ESettingsProfileComponents.PASSWORD]);

export const makeSelectSettingsProfileParamsQuestion = (): OutputSelector<
    IRootReducer,
    ISettingsProfileParamsQuestion,
    (state: ISettingsProfileReducer) => ISettingsProfileParamsQuestion
> => createSelector(makeSelectSettingsProfileState, (state) => state.params[ESettingsProfileComponents.QUESTION]);

/*
 * -------------------------------------------
 *           IS LOADING MODAL
 * -------------------------------------------
 */

export const makeSelectSettingsProfileIsLoadingModal = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: ISettingsProfileReducer) => boolean
> => createSelector(makeSelectSettingsProfileState, (state) => state.isLoadingModal);

/*
 * -------------------------------------------
 *           IS RECOVERY AVAILABLE
 * -------------------------------------------
 */

export const makeSelectSettingsProfileIsRecoveryAvailable = (): OutputSelector<
    IRootReducer,
    boolean,
    (state: ISettingsProfileReducer) => boolean
> => createSelector(makeSelectSettingsProfileState, (state) => state.isRecoveryAvailable);
